import { useCallback, useId, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { DateTime } from 'luxon';
import { z } from 'zod';
import {
  getListAPIKeysQueryKey,
  useCreateAPIKey,
  type APIKeyCreatedDTO,
} from '@/api/trafikportalen';
import { createAPIKeyBodyNameMax } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { DialogWindow } from '@/components/Dialog';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { getApiErrorMessage } from '@/helpers/apiError';
import { dateInputToExpiresAt } from '@/helpers/apiKey';
import { toast } from '@/hooks/useToast';
import { WarningBanner } from '../core/WarningBanner';

const createApiKeyFormSchema = (formatMessage: IntlShape['formatMessage']) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(
          1,
          formatMessage({
            defaultMessage: 'Fältet är obligatoriskt',
          }),
        )
        .max(
          createAPIKeyBodyNameMax,
          formatMessage(
            { defaultMessage: 'Namnet får vara högst {max} tecken' },
            { max: createAPIKeyBodyNameMax },
          ),
        ),
      expires_on: z.string(),
    })
    .superRefine((value, ctx) => {
      if (!value.expires_on) {
        return;
      }

      const expiresAt = dateInputToExpiresAt(value.expires_on);
      if (!expiresAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['expires_on'],
          message: formatMessage({
            defaultMessage: 'Ogiltigt datum',
          }),
        });
        return;
      }

      if (DateTime.fromISO(expiresAt) <= DateTime.now()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['expires_on'],
          message: formatMessage({
            defaultMessage: 'Utgångsdatum måste ligga i framtiden',
          }),
        });
      }
    });

type CreateApiKeyFormValues = z.infer<
  ReturnType<typeof createApiKeyFormSchema>
>;

interface CreateApiKeyDialogProps {
  onClose: () => void;
  onCreated: (apiKey: APIKeyCreatedDTO) => void;
}

export const CreateApiKeyDialog = ({
  onClose,
  onCreated,
}: CreateApiKeyDialogProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { mutateAsync, reset, isPending } = useCreateAPIKey();
  const nameId = useId();
  const expiresId = useId();
  const schema = useMemo(
    () => createApiKeyFormSchema(formatMessage),
    [formatMessage],
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateApiKeyFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      expires_on: '',
    },
  });

  const onSubmit = useCallback(
    async (values: CreateApiKeyFormValues) => {
      const expiresAt = values.expires_on
        ? dateInputToExpiresAt(values.expires_on)
        : null;

      try {
        const created = await mutateAsync({
          data: {
            name: values.name,
            ...(expiresAt ? { expires_at: expiresAt } : {}),
          },
        });
        reset();
        onCreated(created);
        void queryClient.invalidateQueries({
          queryKey: getListAPIKeysQueryKey(),
        });
      } catch (error) {
        toast.error({
          title: formatMessage({
            defaultMessage: 'API-nyckeln kunde inte skapas',
          }),
          description: getApiErrorMessage(
            error,
            formatMessage({
              defaultMessage: 'Något gick fel',
            }),
          ),
        });
      }
    },
    [formatMessage, mutateAsync, onCreated, queryClient, reset],
  );

  const today = DateTime.now().toFormat('yyyy-MM-dd');
  const nameField = register('name');
  const expiresField = register('expires_on');

  return (
    <DialogWindow
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      preventClose={isPending}
      className="max-w-[520px]"
      title={formatMessage({ defaultMessage: 'Skapa API-nyckel' })}
      description={formatMessage({
        defaultMessage:
          'Namn och utgångsdatum går inte att ändra efter att nyckeln skapats, men det går att manuellt återkalla aktiva nycklar när som helst.',
      })}
      content={
        <form
          className="flex flex-col gap-4"
          noValidate
          aria-busy={isPending}
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
        >
          <div className="pt-6">
            <Input
              id={nameId}
              type="text"
              required
              label={formatMessage({ defaultMessage: 'Namn' })}
              placeholder={formatMessage({
                defaultMessage: 'Till exempel fakturaintegration',
              })}
              maxLength={createAPIKeyBodyNameMax}
              autoComplete="off"
              disabled={isPending}
              error={errors.name?.message}
              helperText={formatMessage(
                { defaultMessage: 'Högst {max} tecken' },
                { max: createAPIKeyBodyNameMax },
              )}
              helperTextReserveSpace
              name={nameField.name}
              ref={nameField.ref}
              onBlur={nameField.onBlur}
              onChange={nameField.onChange}
            />
          </div>
          <div className="pt-6">
            <Input
              id={expiresId}
              type="date"
              label={formatMessage({ defaultMessage: 'Utgångsdatum' })}
              min={today}
              disabled={isPending}
              error={errors.expires_on?.message}
              helperText={formatMessage({
                defaultMessage:
                  'Lämna tomt om nyckeln inte ska gå ut. Den slutar fungera vid slutet av valt datum.',
              })}
              helperTextReserveSpace
              name={expiresField.name}
              ref={expiresField.ref}
              onBlur={expiresField.onBlur}
              onChange={expiresField.onChange}
            />
          </div>
          <WarningBanner>
            <FormattedMessage defaultMessage="Nyckeln som du skapar kommer bara visas komplett i nästa steg. Det finns därefter inget sätt att få se hela nyckeln igen." />
          </WarningBanner>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isPending}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button type="submit" variant="filled" loading={isPending}>
              <FormattedMessage defaultMessage="Skapa" />
            </Button>
          </div>
        </form>
      }
    />
  );
};
